import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { normalizeCrmStudioSettings } from '../../config/crmStudio';
import {
  fetchCrmStudioSettings,
  saveCrmStudioSettings,
} from '../../services/crmStudioSettingsService';

const CrmStudioSettingsForm = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const settings = await fetchCrmStudioSettings();
        if (cancelled) return;
        const s = normalizeCrmStudioSettings(settings);
        setBaseUrl(s.baseUrl);
        setIsEnabled(s.isEnabled);
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error(t('adminIntegrations.studio.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const previewUrl =
    baseUrl.trim() && isEnabled
      ? `${baseUrl.trim().replace(/\/+$/, '')}/{patientId}/{caseId}`
      : null;

  const handleSave = async () => {
    if (!baseUrl.trim() && isEnabled) {
      toast.error(t('adminIntegrations.studio.baseUrlRequired'));
      return;
    }
    try {
      setSaving(true);
      await saveCrmStudioSettings({
        baseUrl,
        isEnabled,
        allowedRoleTypes: [],
        allowedPhaseKeys: [],
      });
      toast.success(t('adminIntegrations.studio.saveSuccess'));
    } catch (err) {
      console.error(err);
      toast.error(t('adminIntegrations.studio.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {t('adminIntegrations.studio.title')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('adminIntegrations.studio.description')}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {t('adminIntegrations.studio.workflowHint')}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border border-input"
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
          />
          {t('adminIntegrations.studio.enabled')}
        </label>
      </div>

      <Input
        label={t('adminIntegrations.studio.baseUrlLabel')}
        description={t('adminIntegrations.studio.baseUrlHint')}
        value={baseUrl}
        onChange={(e) => setBaseUrl(e.target.value)}
        placeholder="https://example.com/crmstudio"
        disabled={!isEnabled}
      />

      {previewUrl && (
        <p className="text-xs text-muted-foreground font-mono break-all">
          {t('adminIntegrations.studio.urlPattern')}: {previewUrl}
        </p>
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} loading={saving} disabled={saving}>
          {t('adminIntegrations.studio.save')}
        </Button>
      </div>
    </div>
  );
};

export default CrmStudioSettingsForm;
